const URL_LIMIT = 6000;

export const DEFAULT_AI_CHAT_URL = "https://claude.ai/new?q={prompt}";

const HTTP_URL = /^https?:\/\//i;

export function buildTargetUrl(template: string, prompt: string): { url: string; prefilled: boolean } {
  if (!template.includes("{prompt}")) return { url: template, prefilled: false };
  const full = template.replaceAll("{prompt}", encodeURIComponent(prompt));
  if (full.length <= URL_LIMIT) return { url: full, prefilled: true };
  const idx = template.indexOf("{prompt}");
  const qIdx = template.lastIndexOf("?", idx);
  const base = qIdx >= 0 ? template.slice(0, qIdx) : template.slice(0, idx);
  return { url: base, prefilled: false };
}

export type ShareResult = "shared" | "cancelled" | "copied-opened" | "copied-blank";

/**
 * Whether this device should hand off via the native share sheet (mobile) rather than
 * the clipboard + open-chat fallback (desktop). Coarse pointer / touch + Web Share API.
 */
export function prefersNativeShare(): boolean {
  if (typeof navigator === "undefined") return false;
  const coarse =
    (typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches) ||
    navigator.maxTouchPoints > 1;
  return typeof navigator.share === "function" && coarse;
}

export async function shareTask(prompt: string, targetUrl: string): Promise<ShareResult> {
  if (prefersNativeShare()) {
    try {
      await navigator.share({ text: prompt });
      return "shared";
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return "cancelled";
      // otherwise fall through to the clipboard + open fallback
    }
  }
  try {
    await navigator.clipboard.writeText(prompt);
  } catch {
    // clipboard denied (e.g. non-HTTPS) — still open the chat
  }
  const { url, prefilled } = buildTargetUrl(targetUrl, prompt);
  if (!HTTP_URL.test(url)) return "copied-blank";
  window.open(url, "_blank", "noopener");
  return prefilled ? "copied-opened" : "copied-blank";
}
